import type { Clause, Contract, RiskBreakdownLine, RiskFinding } from '@/types/contract'
import type { RiskProvenance, RiskTier } from './risk'
import { riskHeadline, riskOverrideOf, tierLabel } from './risk'

/**
 * The risk detail modal's presentation logic, as pure data (ADR 0023 detail layer). The modal
 * answers one question — "why is this contract at this tier?" — and the answer is the set of
 * baseline rules that fired. Everything shaped here is what the modal renders; nothing here
 * computes a tier (the server owns tiers, ADR 0026 / risk-tier-switchover D5).
 *
 * Kept out of the component so the wording, the grouping and — above all — the ORDER can be
 * tested: the reader scans the modal top-down and stops early, so what leads has to be what cost
 * the most.
 */

/** One finding as a modal row. */
export interface FindingRow {
  /** The rule definition's stable key — identity across evaluations and re-labels. */
  key: string
  /** What the row says. Falls back to the key: a label-less rule still has to be nameable. */
  label: string
  points: number
  /** Gap/detail weight (ADR 0026); null for floors, which raise tiers instead of scoring. */
  weight: RiskTier | null
  /** The tier a floor raised its clause to; null for gap/detail. */
  minTier: RiskTier | null
  /** What KIND of claim this row is making, in words. */
  kindBadge: string
  /** The clause a floor firing raised — what makes "go to clause" and clause scoping possible. */
  clauseUuid: string | null
}

/** One group of findings under a heading, in render order. */
export interface FindingGroup {
  /** Display heading — a capitalised category, "All categories", or the floors heading. */
  category: string
  /** Total points the group added to the contract's score (0 for the floors group). */
  points: number
  /** True for the clause-floor group, which is a different kind of statement, not a cheaper one. */
  isFloors: boolean
  findings: FindingRow[]
}

const FLOORS_HEADING = 'Clause tier floors'
const UNSCOPED_HEADING = 'All categories'

/** What each rule kind is claiming, in the reader's words rather than the config's. */
const KIND_BADGE: Record<string, string> = {
  gap: 'Protection gap',
  detail: 'Extracted detail',
  floor: 'Clause floor',
}

/**
 * Groups the findings the way the modal reads them: by category, categories ordered by the points
 * they contributed (descending, then alphabetically so the order is stable between reads of an
 * unchanged evaluation), with the clause floors ALWAYS last.
 *
 * Floors go last by kind, not by score. A floor adds no points at all, so ranking it with the
 * scoring groups would place it last by accident on most contracts and first on a contract where
 * nothing else fired — the reader would have to work out from position alone that it is a
 * different kind of claim ("this clause cannot be rated better than MEDIUM_RISK") rather than a
 * contribution to the score.
 */
export function groupFindings(findings: RiskFinding[] | null | undefined): FindingGroup[] {
  if (findings == null || findings.length === 0) return []

  const byHeading = new Map<string, FindingGroup>()
  for (const finding of findings) {
    const isFloor = finding.kind === 'floor'
    const heading = isFloor ? FLOORS_HEADING : categoryHeading(finding.category)
    let group = byHeading.get(heading)
    if (!group) {
      group = { category: heading, points: 0, isFloors: isFloor, findings: [] }
      byHeading.set(heading, group)
    }
    group.points += finding.points ?? 0
    group.findings.push({
      key: finding.key,
      label: finding.label?.trim() || finding.key,
      points: finding.points ?? 0,
      weight: finding.weight ?? null,
      minTier: finding.minTier ?? null,
      kindBadge: KIND_BADGE[finding.kind] ?? finding.kind,
      clauseUuid: finding.clauseUuid ?? null,
    })
  }

  const groups = [...byHeading.values()]
  for (const group of groups) {
    group.findings.sort((a, b) => b.points - a.points || a.label.localeCompare(b.label))
  }
  groups.sort((a, b) =>
    Number(a.isFloors) - Number(b.isFloors)
    || b.points - a.points
    || a.category.localeCompare(b.category),
  )
  return groups
}

/** A stored category token as a heading; a rule scoped to no category applies to all of them. */
function categoryHeading(category: string | null | undefined): string {
  const trimmed = category?.trim()
  if (!trimmed) return UNSCOPED_HEADING
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
}

/** The risk a modal is opened on: the tier, the score behind it, and how it was decided. */
export interface RiskModalSubject {
  tier: RiskTier | null
  score?: number | null
  provenance: RiskProvenance | null | undefined
}

/**
 * The modal's title: the tier, the score behind it, and — when the risk policy moved the tier off
 * its baseline — which way it moved and from where. Composed from the same
 * {@link riskOverrideOf} reading the badge uses, so the hover the reader clicked and the modal it
 * opened can never word the same override differently.
 *
 * A never-evaluated contract gets the bare "Risk: Not assessed": there is no score to quote, and
 * quoting a zero would read as "assessed, and fine".
 */
export function riskModalTitle({ tier, score, provenance }: RiskModalSubject): string {
  const head = riskHeadline(tier)
  if (tier == null) return head

  // `overrideWord` already encodes the direction (downgraded vs escalated).
  const { override, overrideWord } = riskOverrideOf(tier, provenance)
  const scored = score == null ? head : `${head} · score ${score}`
  return override
    ? `${scored} — ${overrideWord} by policy from ${tierLabel(provenance?.baselineTier ?? null)}`
    : scored
}

/**
 * The provenance footer: which published versions decided this. Evaluations that predate version
 * provenance — or that ran against the built-in defaults with no policy — carry neither, and the
 * honest rendering of that is nothing at all rather than "config: unknown".
 */
export function riskProvenanceLine(
  configVersionId: string | null | undefined,
  policyVersionId: string | null | undefined,
): string | null {
  const parts: string[] = []
  if (configVersionId?.trim()) parts.push(`Baseline config ${configVersionId.trim()}`)
  if (policyVersionId?.trim()) parts.push(`Risk policy ${policyVersionId.trim()}`)
  return parts.length === 0 ? null : parts.join(' · ')
}

/**
 * The findings a clause-scoped modal shows: the floor firings on that clause. Contract-level gap
 * and detail firings deliberately do NOT appear — they are statements about the agreement, and
 * showing them under a clause heading would claim this clause caused them.
 */
export function findingsForClause(
  findings: RiskFinding[] | null | undefined,
  clauseUuid: string,
): RiskFinding[] {
  return (findings ?? []).filter((f) => f.clauseUuid === clauseUuid)
}

/** A reason line with the points it scored, when the evaluation recorded them. */
export interface AnnotatedReason {
  text: string
  /** Null when this evaluation predates the Risk Score Breakdown (re-assess to get them). */
  points: number | null
}

/**
 * Pairs the capped reason lines with their served points (Risk Score Breakdown, admin view).
 * Points attach only when the two arrays are index-aligned — anything else (old evaluation,
 * ragged data) renders as plain reasons rather than a misattributed number.
 */
export function annotateReasons(
  reasons: string[],
  reasonPoints: number[] | null | undefined,
): AnnotatedReason[] {
  const aligned = Array.isArray(reasonPoints) && reasonPoints.length === reasons.length
  return reasons.map((text, i) => ({ text, points: aligned ? reasonPoints[i] : null }))
}

/** One clause's row in the admin "Clause risks" section, mirroring the admin dry-run rail. */
export interface ClauseRiskRow {
  clauseUuid: string
  heading: string
  score: number | null
  tier: RiskTier | null
  /** Uncapped per-line attribution; null on evaluations that predate it. */
  breakdown: RiskBreakdownLine[] | null
  /** Fallback when there is no breakdown: the capped reasons, unnumbered. */
  reasons: string[]
}

/**
 * The clauses worth a row: those where the evaluation found something — points scored, or a
 * reason/breakdown line surfaced without points (matching the admin rail's "flagged" reading).
 * Costliest first, ties alphabetical, so the reader who stops early saw what mattered.
 */
export function clauseRiskRows(clauses: Clause[] | null | undefined): ClauseRiskRow[] {
  const rows: ClauseRiskRow[] = []
  for (const clause of clauses ?? []) {
    const score = clause.riskScore ?? null
    const breakdown = clause.riskBreakdown ?? null
    const reasons = (clause.riskReasons ?? []).filter((r): r is string => typeof r === 'string' && r.length > 0)
    if ((score ?? 0) > 0 || (breakdown?.length ?? 0) > 0 || reasons.length > 0) {
      rows.push({
        clauseUuid: clause.uuid,
        heading: clause.heading?.trim() || 'Untitled clause',
        score,
        tier: clause.riskTier ?? null,
        breakdown,
        reasons,
      })
    }
  }
  rows.sort((a, b) => (b.score ?? 0) - (a.score ?? 0) || a.heading.localeCompare(b.heading))
  return rows
}

/** The contract score restated as the sum the reader can check: clause points + rule points. */
export interface RiskRollup {
  total: number
  /** The two addends — present only when they actually sum to the total (mixed-age data won't). */
  parts: { clausePoints: number; rulePoints: number } | null
}

/**
 * The admin rollup line: contract score = Σ clause scores + Σ gap/detail firing points. The parts
 * are shown only when the arithmetic verifiably holds — a contract evaluated before clause scores
 * were served (or with clauses loaded from another read) gets the bare total instead of an
 * equation that doesn't add up.
 */
export function riskRollup(contract: Contract): RiskRollup | null {
  const total = contract.riskScore ?? null
  if (total == null) return null
  const clauses = contract.clauses ?? []
  const scored = clauses.filter((c) => typeof c.riskScore === 'number')
  const clausePoints = scored.reduce((n, c) => n + (c.riskScore ?? 0), 0)
  const rulePoints = (contract.riskFindings ?? [])
    .filter((f) => f.kind !== 'floor')
    .reduce((n, f) => n + (f.points ?? 0), 0)
  const parts = scored.length > 0 && clausePoints + rulePoints === total
    ? { clausePoints, rulePoints }
    : null
  return { total, parts }
}

/**
 * Zips the server's Risk Score Breakdown parallel arrays back into lines (Neo4j properties cannot
 * nest maps, so the store persists texts/points/kinds side by side). Null unless all three arrays
 * arrived with matching lengths — a partial or ragged set means an evaluation this client cannot
 * honestly attribute, and "no attribution" renders better than a misaligned one.
 */
export function zipRiskBreakdown(
  texts: unknown,
  points: unknown,
  kinds: unknown,
): RiskBreakdownLine[] | null {
  if (!Array.isArray(texts) || !Array.isArray(points) || !Array.isArray(kinds)) return null
  if (texts.length !== points.length || texts.length !== kinds.length) return null
  return texts.map((text, i) => ({
    text: typeof text === 'string' ? text : String(text ?? ''),
    points: typeof points[i] === 'number' ? points[i] : Number(points[i] ?? 0),
    kind: kinds[i] === 'floor' ? 'floor' : 'issue',
  }))
}
